import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/useAuth';
import { authAPI } from '@/services/api';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { AuthShell } from '@/components/layout/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ResetPasswordPage() {
  const { lang } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error(t('password_min_length', lang));
      return;
    }
    if (newPassword !== confirm) {
      toast.error(t('passwords_do_not_match', lang));
      return;
    }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      toast.success(t('reset_password_success', lang));
      navigate('/login');
    } catch (err) {
      toast.error(formatApiError(err, t('reset_password_error', lang)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      lang={lang}
      title={t('reset_password_title', lang)}
      description={t('reset_password_subtitle', lang)}
    >
      {tokenMissing ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-foreground">
            {t('reset_password_invalid_token', lang)}
          </div>
          <Link
            to="/forgot-password"
            className="inline-block text-sm font-medium text-primary hover:underline"
          >
            {t('request_new_link', lang)}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="new_password">{t('new_password', lang)}</Label>
            <Input
              id="new_password"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              minLength={8}
              required
            />
            <p className="text-xs text-muted-foreground">
              {t('password_min_length', lang)}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm_password">{t('confirm_password', lang)}</Label>
            <Input
              id="confirm_password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              minLength={8}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} size="lg">
            {loading ? t('saving', lang) : t('reset_password_action', lang)}
          </Button>
          <div className="text-center text-sm text-muted-foreground">
            <Link to="/login" className="font-medium text-primary hover:underline">
              {t('back_to_login', lang)}
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
